import { useFullscreen } from './useFullscreen.js';
import { useWakeLock } from './useWakeLock.js';
import styles from './KioskToggle.module.css';

/**
 * Display-Mode toggle (fullscreen + wake-lock). Round glass button
 * sized to be obvious in the bottom-right cluster. The icon flips
 * between an "expand" diagonal-arrows glyph (enter) and a "collapse"
 * inward-arrows glyph (leave).
 *
 * ESC exits browser fullscreen natively — the fullscreenchange event
 * inside useFullscreen() updates `isFullscreen` so the icon flips back.
 */
export function KioskToggle() {
  const fs = useFullscreen();
  const wl = useWakeLock();

  if (!fs.supported && !wl.supported) return null;

  const isKiosk = fs.isFullscreen;

  async function toggle() {
    if (isKiosk) {
      await fs.exit();
      await wl.release();
    } else {
      await fs.enter();
      await wl.request();
    }
  }

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      className={styles.kioskToggle}
      aria-pressed={isKiosk}
      aria-label={isKiosk ? 'Leave fullscreen (or press Esc)' : 'Enter fullscreen'}
      data-testid="kiosk-toggle"
      title={isKiosk ? 'Leave fullscreen — Esc' : 'Enter fullscreen'}
    >
      {isKiosk ? <CollapseIcon /> : <ExpandIcon />}
    </button>
  );
}

function ExpandIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 9V4h5" />
      <path d="M20 9V4h-5" />
      <path d="M4 15v5h5" />
      <path d="M20 15v5h-5" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 4v5H4" />
      <path d="M15 4v5h5" />
      <path d="M9 20v-5H4" />
      <path d="M15 20v-5h5" />
    </svg>
  );
}
